// undian.repo — akses data program undian doorprize survei.
import { supabase } from '@core/supabase';
import { currentUser } from '@core/session';
import { isoOf } from '@domain/format';
import { undian } from '@core/settings';
import { addExpense } from '@modules/finance/finance.repo';

export interface Draw {
  id: number; periode: string; outlet: string | null; hadiah: string; nilai: number;
  winner_ticket: string | null; winner_nama: string | null; winner_hp: string | null;
  jml_peserta: number; bukti: string | null; diundi_at: string; diundi_by: string | null;
  diserahkan: boolean; serah_at: string | null; serah_by: string | null; expense_id: number | null; catatan: string | null;
}
export interface UndiHasil { ok: boolean; pesan?: string; id?: number; no?: string; winner_nama?: string; winner_hp?: string; jml?: number; hadiah?: string; nilai?: number }

export interface Peserta { no: string; nama: string; hp: string }
/** Daftar peserta undian (HP sudah di-mask di server) — untuk tampilan + sumber animasi lottery. */
export async function listPeserta(periode: string, outlet: string): Promise<Peserta[]> {
  const { data, error } = await supabase().rpc('undian_peserta', { p_periode: periode, p_outlet: outlet });
  if (error) throw new Error(error.message);
  const r = data as { ok?: boolean; pesan?: string; peserta?: Peserta[] } | null;
  if (!r?.ok) throw new Error(r?.pesan ?? 'Gagal memuat peserta.');
  return r.peserta ?? [];
}

/** Satu draw utk halaman tab undi: nama peserta (snapshot) + pemenang + hadiah. RLS: O/M/Finance. */
export async function getDraw(id: number): Promise<{ winner: string; hadiah: string; peserta: string[] } | null> {
  const { data, error } = await supabase().from('undian_draws')
    .select('winner_nama,hadiah,peserta_snapshot').eq('id', id).maybeSingle();
  if (error) throw new Error(error.message);
  if (!data) return null;
  const d = data as { winner_nama: string | null; hadiah: string | null; peserta_snapshot: { nama: string }[] | null };
  return { winner: d.winner_nama ?? '?', hadiah: d.hadiah ?? 'Doorprize', peserta: (d.peserta_snapshot ?? []).map((p) => p.nama).filter(Boolean) };
}

export async function listDraws(periode?: string): Promise<Draw[]> {
  let q = supabase().from('undian_draws').select('*').order('diundi_at', { ascending: false });
  if (periode) q = q.eq('periode', periode);
  const { data, error } = await q;
  if (error) throw new Error(error.message);
  return (data ?? []) as Draw[];
}

/** Undi 1 pemenang acak (server-side, RPC memvalidasi peran O/M). */
export async function undi(periode: string, outlet: string): Promise<UndiHasil> {
  const { data, error } = await supabase().rpc('undian_undi', { p_periode: periode, p_outlet: outlet });
  if (error) throw new Error(error.message);
  return (data ?? { ok: false, pesan: 'Tidak ada balasan.' }) as UndiHasil;
}

// ---- Video bukti undi (Supabase Storage, bucket privat 'undian' — video s/d 100MB; O/M via RLS is_mgr) ----
/** Upload video bukti undi → simpan PATH storage di kolom bukti. Bisa dihapus manual. */
export async function simpanBuktiVideo(id: number, file: File): Promise<void> {
  const ext = ((file.name.split('.').pop() ?? 'mp4').toLowerCase().replace(/[^a-z0-9]/g, '')) || 'mp4';
  const ct = /^video\//.test(file.type) ? file.type : 'video/mp4'; // contentType aman (bukan file.type mentah sembarang)
  const path = `undi/${id}-${crypto.randomUUID()}.${ext}`;
  const { error: upErr } = await supabase().storage.from('undian').upload(path, file, { contentType: ct, upsert: false });
  if (upErr) throw new Error(upErr.message);
  const { error } = await supabase().from('undian_draws').update({ bukti: path }).eq('id', id);
  if (error) { try { await supabase().storage.from('undian').remove([path]); } catch { /* anti-orphan */ } throw new Error(error.message); }
}
/** Signed URL (5 mnt) untuk menonton video bukti. */
export async function buktiVideoUrl(path: string): Promise<string> {
  const { data, error } = await supabase().storage.from('undian').createSignedUrl(path, 300);
  if (error) throw new Error(error.message);
  return data.signedUrl;
}
/** Hapus video bukti (kolom + objek storage). */
export async function hapusBuktiVideo(id: number, path: string): Promise<void> {
  const { error } = await supabase().from('undian_draws').update({ bukti: null }).eq('id', id);
  if (error) throw new Error(error.message);
  try { await supabase().storage.from('undian').remove([path]); } catch { /* best-effort */ }
}

/** Serahkan hadiah → catat BEBAN (kategori dari config) → tandai diserahkan + tautkan expense.
 *  KLAIM ATOMIK dulu (update hanya bila belum diserahkan): kalau 2 klik/2 tab bersamaan, hanya
 *  SATU yang lolos → tak ada Beban dobel. Bila addExpense gagal setelah klaim → revert (biar bisa
 *  diulang, tak jadi "diserahkan tapi tanpa Beban"). */
export async function serahkan(d: Draw): Promise<void> {
  const { data: claimed, error: ce } = await supabase().from('undian_draws')
    .update({ diserahkan: true, serah_at: new Date().toISOString(), serah_by: currentUser()?.nama ?? '' })
    .eq('id', d.id).eq('diserahkan', false).select('id');
  if (ce) throw new Error(ce.message);
  if (!claimed?.length) return; // sudah diserahkan pihak lain → JANGAN buat Beban kedua

  const cfg = undian();
  try {
    const no_dok = await addExpense({
      tgl_iso: isoOf(new Date()), outlet: d.outlet,
      kategori: cfg.kategori || 'Hadiah Loyalti',
      nama: `Doorprize survei: ${d.hadiah}${d.winner_nama ? ' — ' + d.winner_nama : ''}`,
      jumlah: d.nilai || 0,
      catatan: `Undian survei ${d.periode}${d.winner_hp ? ' · ' + d.winner_hp : ''}`,
    });
    if (no_dok) {
      const { data } = await supabase().from('expenses').select('id').eq('no_dok', no_dok).maybeSingle();
      const expenseId = (data as { id: number } | null)?.id ?? null;
      if (expenseId) await supabase().from('undian_draws').update({ expense_id: expenseId }).eq('id', d.id);
    }
  } catch (e) {
    // Beban gagal setelah klaim → balikkan status supaya bisa dicoba lagi.
    // ponytail: bila addExpense COMMIT tapi respons hilang (timeout), retry bisa buat Beban dobel
    // (LOW, jarang). Upgrade saat prod bervolume: cek expense_id/no_dok sebelum re-insert.
    await supabase().from('undian_draws').update({ diserahkan: false, serah_at: null, serah_by: null }).eq('id', d.id);
    throw e;
  }
}
